import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { ReportMultiSelect } from '../../components/reports/ReportMultiSelect';
import { ReportExportToolbar } from '../../components/reports/ReportExportToolbar';
import { ReportMetric } from '../../components/reports/ReportBadges';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import { setFlashToast } from '../../utils/flashToast';
import { defaultDashboardDateRange, todayIsoDate, validateReportDateRange } from '../../utils/reportDates';
import {
  downloadReportBlob,
  exportUserPerformanceReport,
  searchUserPerformanceReport,
  type UserPerformanceReportFilters,
} from '../../services/userPerformanceReports';
import type { UserPerformanceRow } from '../../data/reports';
import { ReportFilterActions } from './ReportFilterActions';
import { useReportFilterLookups } from './useReportFilterLookups';
import { useUserPerformanceLookups } from './useUserPerformanceLookups';

type Filters = {
  userRoles: string[];
  userIds: string[];
  fileCategories: string[];
  subFileCategories: string[];
  divisions: string[];
  from: string;
  to: string;
};

function createEmptyFilters(): Filters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    userRoles: [],
    userIds: [],
    fileCategories: [],
    subFileCategories: [],
    divisions: [],
    from,
    to,
  };
}

function createEmptyFilterIds(): UserPerformanceReportFilters {
  const { from, to } = defaultDashboardDateRange(1);
  return {
    roleIds: [],
    userIds: [],
    categoryIds: [],
    subCategoryIds: [],
    divisionIds: [],
    dateFrom: from,
    dateTo: to,
  };
}

function exportFilename(format: 'pdf' | 'excel'): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return format === 'pdf' ? `user-performance-report-${stamp}.pdf` : `user-performance-report-${stamp}.xlsx`;
}

const METRIC_COLUMN_CLASS = 'rpt-col-metric';

export default function UserPerformanceReportPage() {
  const { t } = useTranslation();
  const maxDate = todayIsoDate();
  const [draft, setDraft] = useState<Filters>(createEmptyFilters);
  const [appliedFilterIds, setAppliedFilterIds] = useState<UserPerformanceReportFilters>(createEmptyFilterIds);
  const [rows, setRows] = useState<UserPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const {
    userOptionItems,
    divisionNames,
    roleNames,
    mapRoleNamesToIds,
    mapDivisionNamesToIds,
    filterSelectedUserIds,
    lookupsLoading: userPerfLookupsLoading,
    usersLoading,
  } = useUserPerformanceLookups(draft.userRoles);

  const {
    fileCategoryNames,
    subFileCategoryNames,
    subFileCategoryDisabled,
    mapFileCategoryNamesToIds,
    mapSubFileCategoryNamesToIds,
    lookupsLoading,
    subLookupsLoading,
  } = useReportFilterLookups(draft.fileCategories);

  const filterLookupsLoading = lookupsLoading || userPerfLookupsLoading;

  const buildFilterIds = useCallback(
    (source: Filters): UserPerformanceReportFilters => ({
      roleIds: mapRoleNamesToIds(source.userRoles),
      userIds: source.userIds,
      categoryIds: mapFileCategoryNamesToIds(source.fileCategories),
      subCategoryIds: mapSubFileCategoryNamesToIds(source.subFileCategories),
      divisionIds: mapDivisionNamesToIds(source.divisions),
      dateFrom: source.from,
      dateTo: source.to,
    }),
    [
      mapDivisionNamesToIds,
      mapFileCategoryNamesToIds,
      mapRoleNamesToIds,
      mapSubFileCategoryNamesToIds,
    ],
  );

  const validateDates = useCallback(
    (from: string, to: string) => {
      const err = validateReportDateRange(from, to);
      if (err) {
        setFlashToast({ message: t(`reports.dateValidation.${err}`), variant: 'error' });
        return false;
      }
      return true;
    },
    [t],
  );

  const runExport = useCallback(
    async (format: 'pdf' | 'excel', source: Filters) => {
      if (!validateDates(source.from, source.to)) return;
      const apiFormat = format === 'pdf' ? 'PDF' : 'EXCEL';
      const res = await exportUserPerformanceReport(apiFormat, buildFilterIds(source));
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.exportFailed'), variant: 'error' });
        throw new Error(res.message ?? 'export failed');
      }
      downloadReportBlob(res.data.blob, res.data.fileName ?? exportFilename(format));
      setFlashToast({ message: t('reports.generateSuccess'), variant: 'success' });
    },
    [buildFilterIds, t, validateDates],
  );

  useEffect(() => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true);

    void searchUserPerformanceReport(appliedFilterIds, page - 1, pageSize).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setFlashToast({ message: res.message ?? t('reports.searchFailed'), variant: 'error' });
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      setRows(res.data.content);
      setTotalCount(res.data.totalElements);
      setTotalPages(Math.max(1, res.data.totalPages));
      setLoading(false);
    });

    return () => controller.abort();
  }, [appliedFilterIds, page, pageSize, searchTrigger, t]);

  const columns = useMemo<Column<UserPerformanceRow>[]>(
    () => [
      {
        key: 'name',
        header: t('reports.upr.colUserName'),
        getValue: (r) => r.name,
        render: (r) => <span className="rpt-td-name">{r.name}</span>,
      },
      { key: 'role', header: t('reports.upr.colUserRoles'), getValue: (r) => r.role },
      { key: 'division', header: t('reports.upr.colDivisionUnit'), getValue: (r) => r.division },
      {
        key: 'assigned',
        header: t('reports.upr.colAssigned'),
        headerClassName: METRIC_COLUMN_CLASS,
        cellClassName: METRIC_COLUMN_CLASS,
        getValue: (r) => String(r.assigned),
        render: (r) => <ReportMetric value={r.assigned} variant="neutral" />,
      },
      {
        key: 'escalated',
        header: t('reports.upr.colEscalated'),
        headerClassName: METRIC_COLUMN_CLASS,
        cellClassName: METRIC_COLUMN_CLASS,
        getValue: (r) => String(r.escalated),
        render: (r) => <ReportMetric value={r.escalated} variant="red" />,
      },
      {
        key: 'completed',
        header: t('reports.upr.colCompleted'),
        headerClassName: METRIC_COLUMN_CLASS,
        cellClassName: METRIC_COLUMN_CLASS,
        getValue: (r) => String(r.completed),
        render: (r) => <ReportMetric value={r.completed} variant="green" />,
      },
    ],
    [t],
  );

  return (
    <>
      <div className="bc">
        <Link to="/reports/user-performance">{t('reports.breadcrumb')}</Link>
        <span className="bc-sep">/</span>
        <span>{t('reports.upr.title')}</span>
      </div>

      <div className="pg-title" style={{ marginBottom: 20 }}>
        {t('reports.upr.title')}
      </div>

      <div className="flt-card rpt-filter-card">
        <div className="rpt-filter-grid-3">
          <ReportMultiSelect
            label={t('reports.filters.userRoles')}
            value={draft.userRoles}
            options={roleNames}
            placeholder={t('reports.filters.selectUserRoles')}
            disabled={filterLookupsLoading}
            onChange={(userRoles) =>
              setDraft((d) => ({
                ...d,
                userRoles,
                userIds: filterSelectedUserIds(d.userIds, userRoles),
              }))
            }
          />
          <ReportMultiSelect
            label={t('reports.filters.userNames')}
            value={draft.userIds}
            optionItems={userOptionItems}
            placeholder={t('reports.filters.selectUserNames')}
            disabled={filterLookupsLoading || usersLoading}
            single
            showCheckboxes={false}
            onChange={(userIds) => setDraft((d) => ({ ...d, userIds }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.fileCategories')}
            value={draft.fileCategories}
            options={fileCategoryNames}
            placeholder={t('reports.filters.selectFileCategories')}
            disabled={lookupsLoading}
            onChange={(fileCategories) =>
              setDraft((d) => ({ ...d, fileCategories, subFileCategories: [] }))
            }
          />
        </div>
        <div className="rpt-filter-grid-2">
          <ReportMultiSelect
            label={t('reports.filters.subFileCategories')}
            value={draft.subFileCategories}
            options={subFileCategoryNames}
            placeholder={t('reports.filters.selectSubFileCategories')}
            disabled={subFileCategoryDisabled || lookupsLoading || subLookupsLoading}
            onChange={(subFileCategories) => setDraft((d) => ({ ...d, subFileCategories }))}
          />
          <ReportMultiSelect
            label={t('reports.filters.divisionsUnits')}
            value={draft.divisions}
            options={divisionNames}
            placeholder={t('reports.filters.selectDivisionsUnits')}
            disabled={filterLookupsLoading}
            onChange={(divisions) => setDraft((d) => ({ ...d, divisions }))}
          />
        </div>
        <div className="rpt-filter-grid-actions">
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.from')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.from}
              maxDate={draft.to || maxDate}
              onChange={(from) => setDraft((d) => ({ ...d, from }))}
            />
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('reports.filters.to')}</div>
            <FormattedDateInput
              className="flt-inp"
              value={draft.to}
              minDate={draft.from || undefined}
              maxDate={maxDate}
              onChange={(to) => setDraft((d) => ({ ...d, to }))}
            />
          </div>
          <ReportFilterActions
            onReset={() => {
              setDraft(createEmptyFilters());
              setAppliedFilterIds(createEmptyFilterIds());
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
            onSearch={() => {
              if (!validateDates(draft.from, draft.to)) return;
              setAppliedFilterIds(buildFilterIds(draft));
              setPage(1);
              setSearchTrigger((n) => n + 1);
            }}
          />
        </div>
      </div>

      <DataTable
        title={t('reports.previewTitle')}
        toolbarRight={
          <ReportExportToolbar
            disabled={filterLookupsLoading}
            onGenerate={(format) => runExport(format, draft)}
          />
        }
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        enableHeaderSearch={false}
        enableSorting={false}
        emphasizedHeader
        rowsPerPageOptions={[10, 30, 50]}
        defaultRowsPerPage={10}
        footerCountNoun={t('reports.records')}
        serverPaging={{
          totalCount,
          totalPages,
          page,
          pageSize,
          onPageChange: (nextPage) => {
            setPage(nextPage);
          },
          onPageSizeChange: (nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          },
        }}
      />
    </>
  );
}
